import { TiptapEditorBase } from '@chiroup/components/form/TiptapEditorBase';
import { tiptapExtensions } from '@chiroup/components/form/tiptapExtensions';
import { Icons } from '@chiroup/components/icons';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import type { JSONContent } from '@tiptap/react';
import { use, useMemo } from 'react';
import { MeContext } from '../../../contexts/me.context';
import { Code } from './Code';
import { Macro } from './Macro';
import codeSuggestion from './codeSuggestion';
import macroSuggestion from './macroSuggestion';

type Props = {
  ref?: any;
  value?: JSONContent | string;
  name?: string;
  label?: string;
  onChange?: (val?: any) => any;
  className?: string;
  containerClassName?: string;
  small?: boolean;
  toolbar?: boolean;
  html?: boolean;
  text?: boolean;
  disabled?: boolean;
  macros?: boolean;
  billingCodes?: boolean;
  /** Page-specific macro items injected into the `/` trigger dropdown.
   *  NOT merged with user macros (macros prop) at this time — use one
   *  or the other. If a future need arises to combine both, merge the
   *  items into a single list for the Macro extension here. */
  addMacros?: { shortcut: string; phrase: string }[];
  extraButtons?: {
    icon: Icons;
    type: 'setContent';
    fn: () => any;
  }[];
  errors?: FormError;
  labelColor?: boolean;
  rightSide?: React.ReactNode;
  /** Appended to the shared extension list for this editor only. */
  extraExtensions?: any[];
  /** Rendered under the editor, inside the Tiptap provider. */
  footer?: React.ReactNode;
  /** Shows the font-size control when the memoFontSize extension is loaded. */
  fontSizes?: number[];
  /** Size unstyled text renders at; shown when no explicit size is applied. */
  defaultFontSize?: number;
};

export const TiptapEditor = ({
  ref,
  value,
  name,
  label,
  className = 'border block w-full border-gray-300 rounded-b-md transition duration-150 ease-in-out sm:leading-5 dark:bg-dark-gray-700 dark:border-dark-gray-600 dark:text-dark-gray-200 outline-none p-2',
  containerClassName,
  onChange,
  small,
  toolbar = true,
  html = false,
  text = false,
  disabled = false,
  macros = false,
  billingCodes = false,
  addMacros,
  extraButtons,
  errors,
  labelColor = false,
  rightSide,
  extraExtensions,
  footer,
  fontSizes,
  defaultFontSize,
}: Props) => {
  const { me } = use(MeContext);

  const extensions = useMemo(() => {
    let copiedExtensions = [...tiptapExtensions];

    if (macros) {
      copiedExtensions.push(
        Macro.configure({
          HTMLAttributes: {
            class: 'macro',
          },
          suggestion: macroSuggestion({ macros: me.macros || [] }),
        })
      );
    }

    if (billingCodes) {
      copiedExtensions = copiedExtensions.filter(i => i.name !== 'codes');
      copiedExtensions.push(
        Code.configure({
          HTMLAttributes: {
            class: 'billing-code rounded-lg border border-gray-300 bg-gray-50 px-1',
          },
          suggestion: codeSuggestion({ clinicId: me.selectedClinic?.ID }),
        })
      );
    }

    if (!macros && addMacros?.length) {
      copiedExtensions.push(
        Macro.configure({
          HTMLAttributes: { class: 'macro' },
          suggestion: macroSuggestion({
            macros: addMacros.map((m, i) => ({
              id: i,
              phrase: m.phrase,
              shortcut: m.shortcut,
            })),
          }),
        })
      );
    }

    if (extraExtensions?.length) {
      copiedExtensions.push(...extraExtensions);
    }

    return copiedExtensions;
  }, [macros, billingCodes, addMacros, extraExtensions, me.macros, me.selectedClinic?.ID]);

  return (
    <TiptapEditorBase
      value={value}
      name={name}
      label={label}
      className={className}
      containerClassName={containerClassName}
      onChange={onChange}
      small={small}
      toolbar={text ? false : toolbar}
      html={html}
      text={text}
      disabled={disabled}
      extraButtons={extraButtons}
      errors={errors}
      labelColor={labelColor}
      extensions={extensions}
      ref={ref}
      rightSide={rightSide}
      footer={footer}
      fontSizes={fontSizes}
      defaultFontSize={defaultFontSize}
    />
  );
};
